import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';

const ADSERVER_URL = process.env.REACT_APP_ADSERVER_URL || '';

const TREATMENT_TYPES = [
  'Detox', 'Inpatient', 'Outpatient', 'Intensive Outpatient (IOP)',
  'Partial Hospitalization (PHP)', 'Sober Living', 'Residential', 'Medication-Assisted Treatment (MAT)',
];

const AddListingPage: React.FC = () => {
  const [form, setForm] = useState({
    name: '', streetAddress: '', city: '', state: '', zipCode: '',
    phone: '', website: '', email: '', description: '',
    treatmentTypes: [] as string[],
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const isMobile = useIsMobile();

  const set = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const toggleTreatment = (t: string) => {
    setForm(f => ({
      ...f,
      treatmentTypes: f.treatmentTypes.includes(t)
        ? f.treatmentTypes.filter(x => x !== t)
        : [...f.treatmentTypes, t],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.city || !form.state || !form.phone || !form.email) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${ADSERVER_URL}/listings/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          address: { streetAddress: form.streetAddress, city: form.city, region: form.state, zipCode: form.zipCode },
          phone: form.phone,
          website: form.website,
          contactEmail: form.email,
          description: form.description,
          treatmentTypes: form.treatmentTypes,
        }),
      });
      if (!res.ok) throw new Error('submit failed');
      setSuccess(true);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
        <h2 style={{ color: '#2e7d32', margin: '0 0 12px' }}>Listing Submitted!</h2>
        <p style={{ color: '#555', marginBottom: '24px', lineHeight: 1.6 }}>
          We'll review your submission and email you at <strong>{form.email}</strong> once it's approved (usually within 1 business day). You'll then be able to claim and manage your listing.
        </p>
        <Link to="/" style={{ padding: '12px 28px', background: '#1a237e', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}>
          Back to Home
        </Link>
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600, color: '#333' };

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 20px' }}>
      <Link to="/advertise" style={{ color: '#1a237e', fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>← Advertise With Us</Link>

      <h1 style={{ margin: '20px 0 8px', fontSize: '28px', color: '#1a237e', fontWeight: 900 }}>Add Your Facility</h1>
      <p style={{ margin: '0 0 32px', color: '#666', fontSize: '15px' }}>
        Submit your treatment center to be listed in our directory. Once approved, you'll receive an email with instructions to claim and manage your profile.
      </p>

      {error && <div style={{ padding: '12px 16px', background: '#ffebee', color: '#c62828', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Facility Name *</label>
          <input style={inputStyle} type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Sunrise Recovery Center" />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Street Address</label>
          <input style={inputStyle} type="text" value={form.streetAddress} onChange={e => set('streetAddress', e.target.value)} placeholder="123 Main St" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 120px 100px', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>City *</label>
            <input style={inputStyle} type="text" value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
          </div>
          <div>
            <label style={labelStyle}>State *</label>
            <input style={inputStyle} type="text" value={form.state} onChange={e => set('state', e.target.value)} placeholder="FL" maxLength={2} />
          </div>
          <div>
            <label style={labelStyle}>ZIP</label>
            <input style={inputStyle} type="text" value={form.zipCode} onChange={e => set('zipCode', e.target.value)} placeholder="33101" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Phone *</label>
            <input style={inputStyle} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" />
          </div>
          <div>
            <label style={labelStyle}>Website</label>
            <input style={inputStyle} type="url" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://yourfacility.com" />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Your Email (for notifications) *</label>
          <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@yourfacility.com" />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Treatment Types</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {TREATMENT_TYPES.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTreatment(t)}
                style={{
                  padding: '6px 14px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer',
                  border: '1px solid #c5cae9',
                  background: form.treatmentTypes.includes(t) ? '#1a237e' : '#fff',
                  color: form.treatmentTypes.includes(t) ? '#fff' : '#333',
                  fontWeight: form.treatmentTypes.includes(t) ? 600 : 400,
                }}
              >{t}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '28px' }}>
          <label style={labelStyle}>Brief Description</label>
          <textarea
            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="Describe your facility, programs, and approach to care..."
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%', padding: '14px', background: submitting ? '#9fa8da' : '#1a237e',
            color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px',
            fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </button>
      </form>
    </div>
  );
};

export default AddListingPage;
